import { generateParams } from 'features/filters/filtersSlice';

import { get, post, postFormData, put } from 'utils/api/api';

import { User } from 'types/auth';
import { BulkActionResultItem } from 'types/commons';
import { Filters } from 'types/filters';
import { Job, JobList, FreeJobsInfo } from 'types/jobs';

import {
  CLIENT_JOBS,
  FREE_JOBS_INFO,
  JOB,
  JOBS_IDS,
  JOBS_JOBLIO_CONTACT_BULK,
  JOBS_STAGE_BULK,
  JOB_LIST,
  MANAGE_JOB,
  TALENT_ACQUISITION_AND_ACE
} from 'constants/endpoints';
import { JobStatus } from 'constants/enums';
import { MAX_ITEMS_TO_GET } from 'constants/uiSettings';

class JobsService {
  async getJobs(params: string): Promise<JobList> {
    const res = await get(`${JOB_LIST}${params}`);
    return res;
  }

  async getActiveJobs(): Promise<Job[]> {
    const params = generateParams({
      status: [JobStatus.Active],
      pageSize: MAX_ITEMS_TO_GET
    });
    const res = await get(`${JOB_LIST}${params}`);
    return res?.content || [];
  }

  async getJobsByClientId(
    clientId: string,
    filters: Filters
  ): Promise<JobList> {
    const params = generateParams(filters);

    const res = await get(`${CLIENT_JOBS}/${clientId}${params}`);
    return res;
  }

  async getJobById(jobId: string): Promise<Job> {
    const res = await get(`${JOB}/${jobId}`);
    return res;
  }

  async updateJobOld(data: Partial<Job>): Promise<void> {
    await put(JOB, data);
  }

  async createJobOld(data: FormData): Promise<void> {
    await postFormData(`${JOB}`, data);
  }

  async createJob(data: Partial<Job>, isDraft: boolean): Promise<void> {
    await post(`${MANAGE_JOB}?draft=${isDraft}`, data);
  }

  async updateJob(data: Partial<Job>, isDraft: boolean): Promise<void> {
    await put(`${MANAGE_JOB}?draft=${isDraft}`, data);
  }

  async changeJobStatus(jobId: string, value: string): Promise<void> {
    await put(`${JOB}/${jobId}/status/${value}`, {});
  }

  async assignRecruiters(
    jobId: string,
    recruitersIds: string[]
  ): Promise<void> {
    const joblioContact = recruitersIds.map((id) => ({ id }));

    await put(`${JOB}/${jobId}/contacts`, {
      joblioContact
    });
  }

  async getTalentAcquisitionAndAce(): Promise<User[]> {
    const response = await get(`${TALENT_ACQUISITION_AND_ACE}`);
    return response?.content || [];
  }

  async getJobsIds(filters: Filters): Promise<string[]> {
    const params = generateParams(filters);
    const response = await get(`${JOBS_IDS}${params}`);
    return response || [];
  }

  async updateJobsStatusBulk(
    ids: string[],
    status: string
  ): Promise<BulkActionResultItem[]> {
    const response = await post(JOBS_STAGE_BULK, {
      ids,
      status
    });
    return response;
  }

  async updateJobsJoblioContactBulk(
    ids: string[],
    joblioContactId: string
  ): Promise<BulkActionResultItem[]> {
    const response = await post(JOBS_JOBLIO_CONTACT_BULK, {
      ids,
      joblioContact: [{ id: joblioContactId }]
    });
    return response;
  }

  async getFreeJobsInfo(): Promise<FreeJobsInfo> {
    const res = await get(FREE_JOBS_INFO);
    return res;
  }
}

export default new JobsService();
